import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ActionMenuComponent } from '@clinic-platform/ui-components';

import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { StatusBadgeComponent } from '../../../shared/ui/status-badge/status-badge.component';
import { StatusBadgeViewModel } from '../../../shared/ui/status-badge/status-badge.models';
import { RescheduleProposalItem } from '../data-access/reschedule-proposals.models';

@Component({
  selector: 'app-reschedule-proposals-table',
  standalone: true,
  imports: [StatusBadgeComponent, TranslatePipe, ActionMenuComponent],
  template: `
    <div class="proposal-table">
      <table class="proposal-table__table">
        <thead>
          <tr>
            <th>{{ 'Client' | t }}</th>
            <th>{{ 'Old Session Time' | t }}</th>
            <th>{{ 'New Session Time' | t }}</th>
            <th>{{ 'common.status' | t }}</th>
            <th>{{ 'Reason' | t }}</th>
            <th>{{ 'Created' | t }}</th>
            <th>{{ 'common.actions' | t }}</th>
          </tr>
        </thead>

        <tbody>
          @for (item of items; track item.id) {
            <tr
              class="proposal-table__row"
              [class.proposal-table__row--selected]="selectedId === item.id"
              (click)="openAppointmentFromRow(item)"
            >
              <td>
                <div class="proposal-table__appointment-summary">
                  @if (canOpenClient && clientId(item)) {
                    <button
                      type="button"
                      class="proposal-table__link-button"
                      (click)="clientOpenClicked.emit(clientId(item)); $event.stopPropagation()"
                      [title]="clientTitle(item)"
                    >
                      {{ clientLabel(item) }}
                    </button>
                  } @else {
                    <div class="proposal-table__client-text" [title]="clientTitle(item)">
                      {{ clientLabel(item) }}
                    </div>
                  }
                </div>
              </td>

              <td class="proposal-table__mono">
                {{ formatSessionWindow(item.oldSession || item.oldPrimarySlot) }}
              </td>

              <td class="proposal-table__mono">
                {{ formatSessionWindow(item.newSession || item.newPrimarySlot) }}
              </td>

              <td>
                <app-status-badge
                  [label]="statusView(item.status).label"
                  [tone]="statusView(item.status).tone"
                />
              </td>

              <td>
                {{ item.reason }}
              </td>

              <td>
                {{ formatDateTime(item.createdAt) || item.createdAt }}
              </td>

              <td>
                <div class="proposal-table__actions" (click)="$event.stopPropagation()">
                  <lib-action-menu
                    buttonAriaLabel="Open reschedule proposal actions"
                    [disabled]="busyId === item.id || !isPending(item.status)"
                    [menuTemplate]="proposalActionsMenu"
                  />

                  <ng-template #proposalActionsMenu let-close="close">
                    <button
                      type="button"
                      role="menuitem"
                      class="action-menu__item action-menu__item--primary"
                      [disabled]="busyId === item.id || !isPending(item.status)"
                      (click)="onAccept(item, close)"
                    >
                      {{ 'action.accept' | t }}
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      class="action-menu__item action-menu__item--danger"
                      [disabled]="busyId === item.id || !isPending(item.status)"
                      (click)="onReject(item, close)"
                    >
                      {{ 'action.reject' | t }}
                    </button>
                  </ng-template>
                </div>
              </td>
            </tr>
          }
        </tbody>
      </table>

      <div class="proposal-table__cards">
        @for (item of items; track item.id) {
          <section
            class="proposal-table__card"
            [class.proposal-table__card--selected]="selectedId === item.id"
            (click)="openAppointmentFromRow(item)"
          >
            <div class="proposal-table__card-head">
              <div class="proposal-table__card-title" [title]="clientTitle(item)">
                {{ clientLabel(item) }}
              </div>
              <app-status-badge
                [label]="statusView(item.status).label"
                [tone]="statusView(item.status).tone"
              />
            </div>

            <div class="proposal-table__card-grid">
              <div class="proposal-table__card-row">
                <div class="proposal-table__card-label">{{ 'Client Profile' | t }}</div>
                <div class="proposal-table__card-value">
                  @if (canOpenClient && clientId(item)) {
                    <button
                      type="button"
                      class="proposal-table__link-button proposal-table__link-button--card"
                      (click)="clientOpenClicked.emit(clientId(item)); $event.stopPropagation()"
                      [title]="clientTitle(item)"
                    >
                      {{ clientLabel(item) }}
                    </button>
                  } @else {
                    {{ clientLabel(item) }}
                  }
                </div>
              </div>

              <div class="proposal-table__card-row">
                <div class="proposal-table__card-label">{{ 'Old Session Time' | t }}</div>
                <div class="proposal-table__card-value">{{ formatSessionWindow(item.oldSession || item.oldPrimarySlot) }}</div>
              </div>

              <div class="proposal-table__card-row">
                <div class="proposal-table__card-label">{{ 'New Session Time' | t }}</div>
                <div class="proposal-table__card-value">{{ formatSessionWindow(item.newSession || item.newPrimarySlot) }}</div>
              </div>

              <div class="proposal-table__card-row">
                <div class="proposal-table__card-label">{{ 'Reason' | t }}</div>
                <div class="proposal-table__card-value">{{ item.reason || '—' }}</div>
              </div>

              <div class="proposal-table__card-row">
                <div class="proposal-table__card-label">{{ 'Created' | t }}</div>
                <div class="proposal-table__card-value">{{ formatDateTime(item.createdAt) || item.createdAt }}</div>
              </div>
            </div>

            <div class="proposal-table__card-actions" (click)="$event.stopPropagation()">
              <lib-action-menu
                buttonAriaLabel="Open reschedule proposal actions"
                [disabled]="busyId === item.id || !isPending(item.status)"
                [menuTemplate]="proposalCardActionsMenu"
              />

              <ng-template #proposalCardActionsMenu let-close="close">
                <button
                  type="button"
                  role="menuitem"
                  class="action-menu__item action-menu__item--primary"
                  [disabled]="busyId === item.id || !isPending(item.status)"
                  (click)="onAccept(item, close)"
                >
                  {{ 'action.accept' | t }}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  class="action-menu__item action-menu__item--danger"
                  [disabled]="busyId === item.id || !isPending(item.status)"
                  (click)="onReject(item, close)"
                >
                  {{ 'action.reject' | t }}
                </button>
              </ng-template>
            </div>
          </section>
        }
      </div>
    </div>
  `,
  styles: [`
    .proposal-table {
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      background: white;
    }

    .proposal-table__table {
      width: 100%;
      border-collapse: collapse;
      min-width: 980px;
    }

    .proposal-table__cards {
      display: none;
    }

    th, td {
      padding: 12px 14px;
      border-bottom: 1px solid #f3f4f6;
      text-align: left;
      font-size: 13px;
    }

    th {
      background: #f9fafb;
      color: #374151;
    }

    td {
      color: #111827;
    }

    .proposal-table__row {
      cursor: pointer;
    }

    .proposal-table__row:hover {
      background: #f9fafb;
    }

    .proposal-table__row--selected {
      background: #eff6ff;
    }

    .proposal-table__appointment-summary {
      display: grid;
      gap: 4px;
      min-width: 0;
    }

    .proposal-table__mono {
      font-family: monospace;
      font-size: 12px;
    }

    .proposal-table__client-text {
      min-width: 0;
      color: #111827;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .proposal-table__link-button {
      all: unset;
      cursor: pointer;
      color: #2563eb;
      text-decoration: underline;
      text-underline-offset: 2px;
      display: inline-block;
      max-width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    @media (max-width: 640px) {
      .proposal-table {
        border: none;
        background: transparent;
      }

      .proposal-table__table {
        display: none;
      }

      .proposal-table__cards {
        display: grid;
        gap: 12px;
      }

      .proposal-table__card {
        border: 1px solid #e5e7eb;
        border-radius: 14px;
        background: #ffffff;
        padding: 12px;
        box-shadow: 0 6px 18px rgba(15, 23, 42, 0.05);
      }

      .proposal-table__card--selected {
        border-color: #bfdbfe;
        background: #f8fbff;
      }

      .proposal-table__card-head {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 10px;
        margin-bottom: 10px;
      }

      .proposal-table__card-title {
        min-width: 0;
        font-size: 14px;
        font-weight: 700;
        color: #111827;
        word-break: break-word;
      }

      .proposal-table__card-grid {
        display: grid;
        gap: 10px;
      }

      .proposal-table__card-row {
        display: grid;
        grid-template-columns: minmax(88px, 108px) minmax(0, 1fr);
        gap: 10px;
        align-items: start;
      }

      .proposal-table__card-label {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: #64748b;
      }

      .proposal-table__card-value {
        min-width: 0;
        font-size: 13px;
        color: #111827;
        word-break: break-word;
      }

      .proposal-table__card-actions {
        margin-top: 12px;
        display: flex;
        justify-content: flex-end;
      }
    }
  `]
})
export class RescheduleProposalsTableComponent
{
  @Input()
  public canOpenClient = false;

  @Input()
  public canOpenAppointment = false;

  @Input()
  public items: RescheduleProposalItem[] = [];

  @Input()
  public busyId: string | null = null;

  @Input()
  public selectedId: string | null = null;

  @Output()
  public readonly rowClicked = new EventEmitter<RescheduleProposalItem>();

  @Output()
  public readonly acceptClicked = new EventEmitter<RescheduleProposalItem>();

  @Output()
  public readonly rejectClicked = new EventEmitter<RescheduleProposalItem>();

  @Output()
  public readonly clientOpenClicked = new EventEmitter<string>();

  @Output()
  public readonly appointmentOpenClicked = new EventEmitter<string>();

  protected onAccept(item: RescheduleProposalItem, close: () => void): void
  {
    if (this.busyId === item.id || !this.isPending(item.status)) {
      return;
    }

    close();
    this.acceptClicked.emit(item);
  }

  protected onReject(item: RescheduleProposalItem, close: () => void): void
  {
    if (this.busyId === item.id || !this.isPending(item.status)) {
      return;
    }

    close();
    this.rejectClicked.emit(item);
  }

  protected openAppointmentFromRow(item: RescheduleProposalItem): void
  {
    this.rowClicked.emit(item);

    const appointmentId = item.appointmentId.trim();
    if (!this.canOpenAppointment || !appointmentId) {
      return;
    }

    this.appointmentOpenClicked.emit(appointmentId);
  }

  protected isPending(status: RescheduleProposalItem['status']): boolean
  {
    return status === 'pending_client_confirmation' || status === 'pending';
  }

  protected clientLabel(item: RescheduleProposalItem): string
  {
    const clientId = item.clientId?.trim() ?? '';
    const clientName = item.clientName?.trim() ?? '';
    const clientEmail = item.clientEmail?.trim() ?? '';
    return clientName || clientEmail || clientId || '—';
  }

  protected clientTitle(item: RescheduleProposalItem): string
  {
    return this.clientLabel(item);
  }

  protected clientId(item: RescheduleProposalItem): string
  {
    return item.clientId?.trim() ?? '';
  }

  protected formatSessionWindow(
    slot: RescheduleProposalItem['oldPrimarySlot'] | undefined
  ): string
  {
    if (!slot)
    {
      return '—';
    }

    const start = this.formatDateTime(slot.startTime);
    const end = this.formatTime(slot.endTime);

    if (!start || !end)
    {
      return '—';
    }

    return `${start} → ${end}`;
  }

  protected statusView(status: RescheduleProposalItem['status']): StatusBadgeViewModel
  {
    switch (status)
    {
      case 'pending_client_confirmation':
      case 'pending':
        return { label: 'status.pending', tone: 'warning' };

      case 'accepted':
        return { label: 'status.accepted', tone: 'success' };

      case 'rejected':
        return { label: 'status.rejected', tone: 'danger' };

      case 'expired':
        return { label: 'status.expired', tone: 'neutral' };

      case 'revoked':
        return { label: 'status.revoked', tone: 'neutral' };

      default:
        return { label: status, tone: 'neutral' };
    }
  }

  protected formatDateTime(value: string): string
  {
    const local = this.formatLocalDateTime(value);
    if (local)
    {
      return local;
    }

    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);

    if (!match)
    {
      return '';
    }

    const [, year, month, day, hours, minutes] = match;
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  }

  private formatTime(value: string): string
  {
    const local = this.formatLocalTime(value);
    if (local)
    {
      return local;
    }

    const match = value.match(/^[\d-]+[T ](\d{2}):(\d{2})/);

    if (!match)
    {
      return '';
    }

    const [, hours, minutes] = match;
    return `${hours}:${minutes}`;
  }

  private formatLocalDateTime(value: string): string
  {
    const normalized = value.trim();
    if (!/[zZ]|[+-]\d{2}:\d{2}$/.test(normalized))
    {
      return '';
    }

    const date = new Date(normalized);
    if (Number.isNaN(date.getTime()))
    {
      return '';
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year} ${this.formatDateLocalTime(date)}`;
  }

  private formatLocalTime(value: string): string
  {
    const normalized = value.trim();
    if (!/[zZ]|[+-]\d{2}:\d{2}$/.test(normalized))
    {
      return '';
    }

    const date = new Date(normalized);
    if (Number.isNaN(date.getTime()))
    {
      return '';
    }

    return this.formatDateLocalTime(date);
  }

  private formatDateLocalTime(date: Date): string
  {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }
}
